import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../api/client';
import type { Lens, UserCaps, UserRole, UserRoleInfo } from '../types';

const ZERO_CAPS: UserCaps = {
  is_admin: false,
  is_resp_rm_oai: false,
  is_resp_rm_ri: false,
  is_resp_rp: false,
  is_rm_oai: false,
  is_rm_ri: false,
  is_rp_oai: false,
  is_rp_ri: false,
};

/** Synthesise a display role string from caps. */
function deriveRole(c: UserCaps): UserRole {
  if (c.is_admin) return 'admin';
  if (c.is_resp_rm_oai && c.is_resp_rm_ri && c.is_resp_rp) return 'resp_all';
  if (c.is_resp_rm_oai) return 'resp_oai';
  if (c.is_resp_rm_ri) return 'resp_ri';
  return 'utilisateur';
}

interface RoleContextValue {
  caps: UserCaps;
  lens: Lens;
  activeLens: Lens;
  availableLenses: Lens[];
  staffId: number | null;
  loading: boolean;
  /** True iff the user has more than one explicit lens to choose from. */
  hasLensChoice: boolean;
  setLens: (next: Lens) => Promise<void>;
  refreshRole: () => Promise<void>;

  // Display helpers derived from caps.
  role: UserRole;
  isAdmin: boolean;
  isResponsable: boolean;
  isUtilisateur: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  caps: ZERO_CAPS,
  lens: 'auto',
  activeLens: 'auto',
  availableLenses: ['auto'],
  staffId: null,
  loading: true,
  hasLensChoice: false,
  setLens: async () => {},
  refreshRole: async () => {},
  role: 'utilisateur',
  isAdmin: false,
  isResponsable: false,
  isUtilisateur: true,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [roleInfo, setRoleInfo] = useState<UserRoleInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRole = useCallback(async () => {
    if (!user) {
      setRoleInfo(null);
      setLoading(false);
      return;
    }
    try {
      const info = await api.getRoleInfo() as UserRoleInfo;
      setRoleInfo(info);
    } catch {
      setRoleInfo({
        caps: { ...ZERO_CAPS, is_admin: !!user.isAdmin },
        lens: 'auto',
        activeLens: user.isAdmin ? 'all' : 'auto',
        availableLenses: user.isAdmin ? ['auto', 'all'] : ['auto'],
        staffId: null,
      });
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchRole(); }, [fetchRole]);

  const caps: UserCaps = roleInfo?.caps ?? ZERO_CAPS;
  const lens: Lens = roleInfo?.lens ?? 'auto';
  const activeLens: Lens = roleInfo?.activeLens ?? 'auto';
  const availableLenses: Lens[] = roleInfo?.availableLenses ?? ['auto'];
  const staffId = roleInfo?.staffId ?? null;

  const role = deriveRole(caps);
  const isAdmin = caps.is_admin;
  const isResponsable = caps.is_admin || caps.is_resp_rm_oai || caps.is_resp_rm_ri || caps.is_resp_rp;
  const isUtilisateur = !isResponsable;

  // The lens dropdown shows iff there's more than one explicit pick. 'auto'
  // resolves to one of the others, so it doesn't count as a distinct choice.
  const explicit = availableLenses.filter((l) => l !== 'auto');
  const hasLensChoice = explicit.length > 1;

  // Bounce off pages we'd 403 on under a portfolio lens.
  const bounceIfPortfolio = (next: Lens) => {
    if (next === 'rm' || next === 'rp') {
      const hash = window.location.hash.replace(/^#/, '');
      if (hash === 'equipe' || hash === 'conseillers') {
        window.location.hash = 'portfolio';
      }
    }
  };

  const setLens = useCallback(async (next: Lens) => {
    bounceIfPortfolio(next);
    const prev = roleInfo;
    setRoleInfo((info) => (info ? { ...info, lens: next, activeLens: next } : info));
    try {
      await api.setLens(next);
      await fetchRole(); // re-resolve activeLens server-side so derived values update.
    } catch (err) {
      setRoleInfo(prev);
      throw err;
    }
  }, [roleInfo, fetchRole]);

  const value: RoleContextValue = {
    caps,
    lens,
    activeLens,
    availableLenses,
    staffId,
    loading,
    hasLensChoice,
    setLens,
    refreshRole: fetchRole,
    role,
    isAdmin,
    isResponsable,
    isUtilisateur,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  return useContext(RoleContext);
}
